import { useState, useRef, useEffect } from 'react';

/** Map course/API lang tags to SpeechSynthesis BCP-47 locales */
const LANG_TO_LOCALE = {
  de: 'de-DE',
  en: 'en-US',
  ja: 'ja-JP',
  id: 'id-ID',
  ko: 'ko-KR',
  fr: 'fr-FR',
  es: 'es-ES',
};

function resolveSpeechLocale(langCode) {
  const raw = String(langCode || 'de').toLowerCase().trim();
  const primary = raw.split(/[-_]/)[0] || 'de';
  return LANG_TO_LOCALE[primary] || `${primary}-${primary.toUpperCase()}`;
}

function pickVoiceForLang(voices, langCode) {
  const primary = String(langCode || 'de').toLowerCase().split(/[-_]/)[0];
  if (!voices?.length || !primary) return null;
  const norm = (l) => String(l || '').toLowerCase().replace('_', '-');
  const exact = voices.find((v) => norm(v.lang) === resolveSpeechLocale(langCode).toLowerCase());
  if (exact) return exact;
  return voices.find((v) => norm(v.lang).startsWith(`${primary}-`) || norm(v.lang) === primary);
}

// Browser TTS fallback when pronun-audio API fails
export const useSpeechSynthesis = (langCode = 'de') => {
  const lang = resolveSpeechLocale(langCode);
  const [isPlayingFullSentence, setIsPlayingFullSentence] = useState(false);
  const [playingWordIndex, setPlayingWordIndex] = useState(null);
  const [loadingWordIndex, setLoadingWordIndex] = useState(null);
  const fullSentenceAudioRef = useRef(null);
  const utteranceRef = useRef(null);
  const voicesLoadedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return;
    }

    const loadVoices = () => {
      try {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          voicesLoadedRef.current = true;
          console.log('Voices loaded:', voices.length, 'voices available');
        }
      } catch (e) {
        console.warn('Error loading voices:', e);
      }
    };

    loadVoices();

    if ('onvoiceschanged' in window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if ('onvoiceschanged' in window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        try {
          window.speechSynthesis.cancel();
        } catch (e) {
          // Ignore
        }
      }
      if (utteranceRef.current) {
        utteranceRef.current = null;
      }
      if (fullSentenceAudioRef.current) {
        fullSentenceAudioRef.current.pause();
        fullSentenceAudioRef.current = null;
      }
      setIsPlayingFullSentence(false);
      setPlayingWordIndex(null);
      setLoadingWordIndex(null);
    };
  }, []);

  const playFullSentence = (text) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window) || !text || !text.trim()) {
      if (!text || !text.trim()) {
        console.warn('useSpeechSynthesis: No text provided');
      }
      return;
    }

    if (isPlayingFullSentence) {
      try {
        window.speechSynthesis.cancel();
        setIsPlayingFullSentence(false);
        utteranceRef.current = null;
      } catch {}
      return;
    }

    try {
      try {
        window.speechSynthesis.cancel();
      } catch {}

      const speakAfterDelay = () => {
        const utterance = new SpeechSynthesisUtterance(text.trim());
        utteranceRef.current = utterance;
        utterance.lang = lang;
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        utterance.volume = 1;
        try {
          const voices = window.speechSynthesis.getVoices?.() || [];
          const targetVoice = pickVoiceForLang(voices, langCode);
          if (targetVoice) {
            utterance.voice = targetVoice;
          } else {
            console.warn('useSpeechSynthesis: no voice for', langCode, '- browser may use wrong language');
          }
        } catch (e) {
          console.warn('useSpeechSynthesis: voice selection failed', e);
        }

        utterance.onstart = () => {
          console.log('Speech started:', text);
          setIsPlayingFullSentence(true);
        };

        utterance.onend = () => {
          console.log('Speech ended');
          setIsPlayingFullSentence(false);
          utteranceRef.current = null;
        };

        utterance.onerror = (event) => {
          console.error('Speech error:', event.error, event);
          setIsPlayingFullSentence(false);
          utteranceRef.current = null;
        };

        console.log('Speaking:', text.trim(), 'lang:', lang);
        window.speechSynthesis.speak(utterance);
      };

      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        setTimeout(speakAfterDelay, 50);
      } else {
        speakAfterDelay();
      }
    } catch (error) {
      console.error('Error in playFullSentence:', error);
      setIsPlayingFullSentence(false);
      utteranceRef.current = null;
    }
  };

  const playWord = (word, index) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window) || !word || !word.trim()) {
      if (!word || !word.trim()) {
        console.warn('useSpeechSynthesis: No word provided');
      }
      return;
    }

    if (playingWordIndex === index || loadingWordIndex === index) {
      try {
        window.speechSynthesis.cancel();
        setPlayingWordIndex(null);
        setLoadingWordIndex(null);
        utteranceRef.current = null;
      } catch {}
      return;
    }

    try {
      try {
        window.speechSynthesis.cancel();
      } catch {}
      setLoadingWordIndex(index);

      const speakAfterDelay = () => {
        const utterance = new SpeechSynthesisUtterance(word.trim());
        utteranceRef.current = utterance;
        utterance.lang = lang;
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        utterance.volume = 1;
        try {
          const voices = window.speechSynthesis.getVoices?.() || [];
          const targetVoice = pickVoiceForLang(voices, langCode);
          if (targetVoice) {
            utterance.voice = targetVoice;
          } else {
            console.warn('useSpeechSynthesis: no voice for', langCode, '- browser may use wrong language');
          }
        } catch (e) {
          console.warn('useSpeechSynthesis: voice selection failed', e);
        }

        utterance.onstart = () => {
          console.log('Word speech started:', word);
          setLoadingWordIndex(null);
          setPlayingWordIndex(index);
        };

        utterance.onend = () => {
          console.log('Word speech ended');
          setLoadingWordIndex(null);
          setPlayingWordIndex(null);
          utteranceRef.current = null;
        };

        utterance.onerror = (event) => {
          console.error('Word speech error:', event.error, event);
          setLoadingWordIndex(null);
          setPlayingWordIndex(null);
          utteranceRef.current = null;
        };

        console.log('Speaking word:', word.trim(), 'lang:', lang);
        window.speechSynthesis.speak(utterance);
      };

      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
        setTimeout(speakAfterDelay, 50);
      } else {
        speakAfterDelay();
      }
    } catch (error) {
      console.error('Error in playWord:', error);
      setLoadingWordIndex(null);
      setPlayingWordIndex(null);
      utteranceRef.current = null;
    }
  };

  const cleanup = () => {
    if (fullSentenceAudioRef.current) {
      fullSentenceAudioRef.current.pause();
      fullSentenceAudioRef.current = null;
    }
    if (utteranceRef.current) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }
  };

  const reset = () => {
    setIsPlayingFullSentence(false);
    setPlayingWordIndex(null);
    setLoadingWordIndex(null);
  };

  return {
    isPlayingFullSentence,
    playingWordIndex,
    loadingWordIndex,
    playFullSentence,
    playWord,
    cleanup,
    reset,
  };
};
